import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";

function convertBengaliToEnglish(str) {
  const map = {
    "০": "0",
    "১": "1",
    "২": "2",
    "৩": "3",
    "৪": "4",
    "৫": "5",
    "৬": "6",
    "৭": "7",
    "৮": "8",
    "৯": "9",
  };
  return str.replace(/[০-৯]/g, (digit) => map[digit]);
}

const EditCategory = ({ isSearchable }) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [categoryData, setCategoryData] = useState({
    translations: {
      en: { name: "", description: "", metaTitle: "", metaDescription: "" },
      bn: { name: "", description: "", metaTitle: "", metaDescription: "" },
    },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset language to English when component mounts or ID changes
  useEffect(() => {
    // Set default language to English
    setSelectedLanguage("en");
    i18n.changeLanguage("en");
  }, [id, i18n]);

  // Fetch category data
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `https://api.muktihospital.com/api/category/${id}?lang=${selectedLanguage}`,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );

        if (response.status === 200) {
          const cat = response.data;
          setCategoryData((prev) => ({
            ...prev,
            translations: {
              ...prev.translations,
              [selectedLanguage]: {
                name: cat.translations.name || "",
                description: cat.translations.description || "",
                metaTitle: cat.translations.metaTitle || "",
                metaDescription: cat.translations.metaDescription || "",
              },
            },
          }));
        } else {
          toast.error("Failed to load category details.");
        }
      } catch (error) {
        console.error("❌ Error fetching category:", error);
        toast.error("Could not load category.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategory();
  }, [id, selectedLanguage]);

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  const handleLanguageChange = (selectedOption) => {
    i18n.changeLanguage(selectedOption.value);
    setSelectedLanguage(selectedOption.value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const convertedValue = convertBengaliToEnglish(value);

    setCategoryData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [selectedLanguage]: {
          ...prev.translations[selectedLanguage],
          [name]: convertedValue,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const dataToSend = { translations: categoryData.translations };
      const response = await axios.put(
        `https://api.muktihospital.com/api/category/edit/${id}`,
        dataToSend,
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            "Content-Type": "application/json",
          },
        }
      );
      // console.log("Response:", response);

      if (response.status === 200) {
        toast.success("Category updated successfully!");
      } else {
        toast.error("Failed to update category.");
      }
    } catch (error) {
      console.error("❌ Error updating category:", error);
      toast.error("An error occurred while updating the category.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton height={30} count={6} />
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Blogs"},
    { label: "Edit Category" },
  ];

  return (
    <div>
      <ToastContainer/>
      <PageHeading title="Edit Category" breadcrumbs={breadcrumbs} />
      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === selectedLanguage)}
          isSearchable={isSearchable}
        />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="bg-white shadow-sm rounded-lg pb-2">
              <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Basic Information
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
                <div>
                  <label className="label">Category Name</label>
                  {isSubmitting ? (
                    <Skeleton height={35} />
                  ) : (
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter Category Name"
                      className="input-field"
                      value={categoryData.translations[selectedLanguage].name}
                      onChange={handleChange}
                    />
                  )}
                </div>
                <div>
                  <label className="label">Category Description</label>
                  {isSubmitting ? (
                    <Skeleton height={35} />
                  ) : (
                    <input
                      type="text"
                      name="description"
                      placeholder="Enter Category Description"
                      className="input-field"
                      value={categoryData.translations[selectedLanguage].description}
                      onChange={handleChange}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-white shadow-sm rounded-lg">
              <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  SEO Configuration
                </h2>
              </div>
              <div className="p-5 grid grid-cols-2 gap-6">
                <div>
                  <label className="label">Meta Title</label>
                  {isSubmitting ? (
                    <Skeleton height={35} />
                  ) : (
                    <input
                      type="text"
                      name="metaTitle"
                      placeholder="Enter Meta Title (SEO)"
                      className="input-field"
                      value={categoryData.translations[selectedLanguage].metaTitle}
                      onChange={handleChange}
                    />
                  )}
                </div>
                <div>
                  <label className="label">Meta Description</label>
                  {isSubmitting ? (
                    <Skeleton height={50} />
                  ) : (
                    <textarea
                      name="metaDescription"
                      placeholder="Enter Meta Description (SEO)"
                      className="input-field"
                      value={categoryData.translations[selectedLanguage].metaDescription}
                      onChange={handleChange}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-4 justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              >
                {isSubmitting ? "Updating..." : "Update Content"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditCategory;